import {
  Input,
  InputDropdown,
  InputDropdownItem,
  isEmpty,
  MultiSelectDropdown,
  santizeInput,
  Spinner,
} from '@platform/components';
import { useParams } from 'wouter';
import {
  BUSINESS_CHALLENGES,
  BusinessChallengeName,
  BusinessChallengeValue,
  LanguagesType,
  ProjectDetailsMode,
  ProjectFormValues,
  SKILL_OPTIONS,
  SkillType,
  SupportType,
} from './consts';
import {
  CreateProjectRequestSupportTypeInfoItem,
  EditProjectParamsOutput,
  ProjectRoleType,
} from '@platform/client/schemas';
import {
  SubmitHandler,
  useForm,
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { FormBlock } from './utils';
import ProjectDetailsForm from './ProjectDetailsForm';
import { useEffect, useState } from 'react';
import { MyProjects, Projects } from '@platform/client';
import { useQueryClient } from '@tanstack/react-query';

const businessChallengeOptions: InputDropdownItem[] = BUSINESS_CHALLENGES.map((bc) => ({
  text: bc.name,
  textValue: bc.value,
})).sort((a, b) => a.text.localeCompare(b.text));

const supportTypeOptions: InputDropdownItem[] = [
  {
    text: 'Call',
    textValue: ProjectRoleType.consultant,
  },
  {
    text: 'Coach',
    textValue: ProjectRoleType.coach,
  },
  {
    text: 'Consultant',
    textValue: ProjectRoleType.doer,
  },
  {
    text: 'Mentor',
    textValue: ProjectRoleType.mentor,
  },
  {
    text: 'Trainer',
    textValue: ProjectRoleType.trainer,
  },
];

const BASE_INFO_HEADER: Record<ProjectDetailsMode, string> = {
  'rep-create': '', // This section doesn't exist in rep-create mode
  'rep-edit': 'Project Basics',
  admin: 'Project Basics',
};

type FormValues = ProjectFormValues & {
  projectName: string;
  businessChallenge: BusinessChallengeValue;
  skills: Set<SkillType>;
  supportTypes: Set<SupportType>;
};

// TODO: Better document the need for this
/** This confusing function converts from an input ProjectRoleType string (lowecase, what's stored in the DB) into a frontend
 * SupportType (capitalized, what's used in the UI). */
const getSupportTypeNameForValue = (value: string): Set<SupportType> => {
  return new Set([
    supportTypeOptions.find((sto) => sto.textValue === value)?.text,
  ]) as FormValues['supportTypes'];
};

const getFormValuesBySupportType = (projectData: EditProjectParamsOutput): Partial<FormValues> => {
  const roleType = projectData.supportTypeInfo.roleType;

  if (roleType === 'trainer') {
    return {
      trainerAdditionalRequirements: projectData.supportTypeInfo.additionalRequirements ?? '',
      trainingTopicRichText: projectData.supportTypeInfo.deliverables,
      trainerLocation: projectData.supportTypeInfo.location,
    };
  } else if (roleType === 'consultant') {
    return {
      callOutcomesRichText: projectData.supportTypeInfo.deliverables,
    };
  } else if (roleType === 'mentor') {
    return {
      mentorOutcomesRichText: projectData.supportTypeInfo.deliverables,
    };
  } else if (roleType === 'coach') {
    return {
      coachOutcomesRichText: projectData.supportTypeInfo.deliverables,
    };
  } else if (roleType === 'doer') {
    return {
      projectTitle: projectData.supportTypeInfo.projectTitle ?? '',
      expectedDeliverablesRichText: projectData.supportTypeInfo.deliverables ?? '',
      consultantAdditionalRequirements: projectData.supportTypeInfo.additionalRequirements ?? '',
      projectTimeCommitment: projectData.supportTypeInfo.timeCommitment ?? '',
      projectBkgRichText: projectData.supportTypeInfo.projectBackground ?? '',
      consultantLocation: projectData.supportTypeInfo.consultantLocation ?? 'remote',
      learningOutcomesRichText: projectData.supportTypeInfo.learningOutcomes ?? '',
    };
  }

  return {};
};

/** TODO: A great deal of this page is duplicate with CreateProjectPage, with much of the UI
 * and data payload building being copy/pasted. We should unify these into one approach with the
 * next design, if possible */
const EditProjectPage = ({ mode }: { mode: 'admin' | 'rep-edit' }) => {
  const { projectId } = useParams<{ projectId: string }>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmissionError, setIsSubmissionError] = useState(false);
  const queryClient = useQueryClient();

  const { data: projectData } = Projects.useGetEditableProject({
    project_id: parseInt(projectId),
  });

  const { setValue, watch, register, handleSubmit, reset } = useForm<FormValues>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      supportTypes: new Set<SupportType>(),
      skills: new Set<SkillType>(),
      languages: [],
      ...(projectData ? getFormValuesBySupportType(projectData) : {}),
    },
  });

  /* Set initial form values via reset() once data loads. We can't
   * do this with RHF's useForm's defaultValues prop as that executes
   * before data loads from the API hook */
  useEffect(() => {
    if (projectData) {
      reset({
        projectName: projectData.name,
        businessChallenge: BUSINESS_CHALLENGES.find(
          (bc) => bc.value === projectData.baseParams.businessChallenge,
        )?.value as BusinessChallengeValue,
        skills: new Set(projectData.baseParams.skills),
        supportTypes: getSupportTypeNameForValue(projectData.supportTypeInfo.roleType),
        languages: projectData.baseParams.languages as LanguagesType[],
        workWithTeam: projectData.baseParams.openToTeam,
        ...(projectData ? getFormValuesBySupportType(projectData) : {}),
      });
    }
  }, [projectData, reset]);

  const editProjectMutator = Projects.useEditProject({
    mutation: {
      onMutate() {
        setIsSubmitting(true);
      },
      onError: () => {
        setIsSubmitting(false);
        setIsSubmissionError(true);
      },
      onSuccess: () => {
        setIsSubmitting(false);
        queryClient.invalidateQueries({
          queryKey: [...MyProjects.getGetOrgUserMyProjectsQueryKey()],
        });
        queryClient.invalidateQueries({
          queryKey: [
            ...Projects.getGetEditableProjectQueryKey({ project_id: parseInt(projectId) }),
          ],
        });
      },
    },
  });

  const onSubmit: SubmitHandler<FormValues> = (data: FormValues) => {
    const supportTypeAnswers: CreateProjectRequestSupportTypeInfoItem[] = [];

    if (data.supportTypes.has('Trainer')) {
      supportTypeAnswers.push({
        roleType: 'trainer',
        additionalRequirements: data.trainerAdditionalRequirements,
        deliverables: santizeInput(data.trainingTopicRichText),
        location: data.trainerLocation,
      });
    }

    if (data.supportTypes.has('Call')) {
      supportTypeAnswers.push({
        // This role type is not a bug; see the documentation for CallProjectParams
        roleType: 'consultant',
        deliverables: santizeInput(data.callOutcomesRichText),
      });
    }

    if (data.supportTypes.has('Mentor')) {
      supportTypeAnswers.push({
        roleType: 'mentor',
        deliverables: santizeInput(data.mentorOutcomesRichText),
      });
    }

    if (data.supportTypes.has('Coach')) {
      supportTypeAnswers.push({
        roleType: 'coach',
        deliverables: santizeInput(data.coachOutcomesRichText),
      });
    }

    if (data.supportTypes.has('Consultant')) {
      supportTypeAnswers.push({
        // This role type is not a bug; see the documentation for ConsultantProjectParams
        roleType: 'doer',
        // We use project name for rep edits as reps don't actually see the "projectName" input.
        // This is a byproduct how consultant type projects allow manually specifying a title
        // upon creation and that this edit UI is non shared with the create project UI.
        projectTitle: mode === 'rep-edit' ? data.projectName : data.projectTitle,
        deliverables: santizeInput(data.expectedDeliverablesRichText),
        additionalRequirements: data.consultantAdditionalRequirements,
        timeCommitment: data.projectTimeCommitment,
        projectBackground: santizeInput(data.projectBkgRichText),
        consultantLocation: data.consultantLocation,
        learningOutcomes: santizeInput(data.learningOutcomesRichText),
      });
    }

    editProjectMutator.mutate({
      params: {
        project_id: parseInt(projectId),
      },
      data: {
        baseParams: {
          businessChallenge: data.businessChallenge,
          skills: Array.from(data.skills),
          languages: data.languages,
          openToTeam: data.workWithTeam,
        },
        name: data.projectName,
        supportTypeInfo: Array.from(supportTypeAnswers)[0],
      },
    });
  };

  if (!projectData) {
    return (
      <div className='mx-auto w-fit'>
        <Spinner />
      </div>
    );
  }

  let isSubmitEnabled = true;

  isSubmitEnabled =
    isSubmitEnabled &&
    watch('projectName')?.length > 0 &&
    watch('languages') &&
    watch('languages').length > 0 &&
    watch('workWithTeam') !== undefined;

  if (watch('supportTypes').has('Trainer')) {
    isSubmitEnabled =
      isSubmitEnabled &&
      !isEmpty(watch('trainingTopicRichText')) &&
      !isEmpty(watch('trainerLocation'));
  }
  if (watch('supportTypes').has('Call')) {
    isSubmitEnabled = isSubmitEnabled && !isEmpty(watch('callOutcomesRichText'));
  }
  if (watch('supportTypes').has('Mentor')) {
    isSubmitEnabled = isSubmitEnabled && !isEmpty(watch('mentorOutcomesRichText'));
  }
  if (watch('supportTypes').has('Coach')) {
    isSubmitEnabled = isSubmitEnabled && !isEmpty(watch('coachOutcomesRichText'));
  }
  if (watch('supportTypes').has('Consultant')) {
    isSubmitEnabled =
      isSubmitEnabled &&
      !isEmpty(watch('expectedDeliverablesRichText')) &&
      !isEmpty(watch('projectTimeCommitment')) &&
      !isEmpty(watch('projectBkgRichText')) &&
      !isEmpty(watch('consultantLocation'));
  }

  return (
    <div className='mx-auto w-2xl'>
      <FormBlock header={BASE_INFO_HEADER[mode]}>
        <Input
          type='inline'
          label='Project name'
          value={watch('projectName') || ''}
          onChange={(e) => {
            setValue('projectName', e.target.value);
          }}
          validate={(val) => (val.length === 0 ? 'Project name is required' : undefined)}
        />
        <InputDropdown
          items={businessChallengeOptions}
          label='Business Challenge'
          selectedKey={
            businessChallengeOptions.find((bco) => bco.textValue === watch('businessChallenge'))
              ?.textValue as BusinessChallengeName
          }
          onSelectionChange={(value) => {
            const newVal = BUSINESS_CHALLENGES.find((bc) => bc.value === value)?.value;
            if (!newVal) return;

            setValue('businessChallenge', newVal as BusinessChallengeValue);
          }}
          type='default'
        />
        <MultiSelectDropdown
          register={() => {
            return register('skills');
          }}
          selectedItems={Array.from(watch('skills') ?? [])}
          onSelectionChange={(s) => setValue('skills', new Set(s as SkillType[]))}
          options={SKILL_OPTIONS}
          placeholder='Select one or more skills'
          label='Skills'
          filterable
          error={
            Array.from(watch('skills')).length === 0 ? 'Must select at least one skill' : undefined
          }
        />
        <InputDropdown
          items={supportTypeOptions}
          label='Support Type/Role'
          selectedKey={
            supportTypeOptions.find((sto) => watch('supportTypes')?.has(sto.text as SupportType))
              ?.textValue as SupportType
          }
          onSelectionChange={(value) => {
            setValue('supportTypes', getSupportTypeNameForValue(value as string));
          }}
          inputDisabled
          type='default'
        />
      </FormBlock>
      <ProjectDetailsForm
        mode='admin'
        setValue={setValue as unknown as UseFormSetValue<ProjectFormValues>}
        watch={watch as unknown as UseFormWatch<ProjectFormValues>}
        register={register as unknown as UseFormRegister<ProjectFormValues>}
        onSubmit={() => handleSubmit(onSubmit)()}
        supportTypes={watch('supportTypes')}
        isSubmitting={isSubmitting}
        setIsSubmitting={setIsSubmitting}
        isSubmitEnabled={isSubmitEnabled}
      />
      {/* TODO: Do we have an established pattern here? Should this navigate back to admin project page? */}
      {editProjectMutator.isSuccess && (
        <div className='mt-8 text-center text-success-600'>Project updated successfully!</div>
      )}
      {isSubmissionError && (
        <div className='mt-8 text-center text-red-600'>
          There was an error submitting your project. Please try again or contact support.
        </div>
      )}
    </div>
  );
};

export default EditProjectPage;
